import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ChevronUp from 'mdi-react/ChevronUpIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import Currency from '@prisjakt/ui-currency';
import { AppContext } from '@prisjakt/ui-app-context';
import { AppState } from '../../../../reducers';
import { VariantGroup, Variant as VariantType } from '../../../../types';
import { SortOptionEnum } from '../sort-options/types';
import { ViewportContext } from '../../../../components/viewport';
import { VARIANT_BUTTON_MAX_WIDTH } from './constants';

import * as S from './styles';

type VariantGroupsProps = {
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
  variantGroups: VariantGroup[];
};

const VariantGroups = ({
  handleSelectedVariant,
  variantGroups,
}: VariantGroupsProps) => (
  <>
    {variantGroups.map(group =>
      group.variants?.length ? (
        <Group handleSelectedVariant={handleSelectedVariant} group={group} />
      ) : null
    )}
  </>
);

type GroupProps = {
  group: VariantGroup;
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
};

const Group = ({ group, handleSelectedVariant }: GroupProps) => {
  const { t } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);

  const [isDesktopContent, setIsDesktopContent] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setIsDesktopContent(Boolean(isDesktop));
  }, [isDesktop]);

  const VISIBLE_VARIANTS_LIMIT = group.variants?.length === 7 ? 7 : 6;

  const currentSort = useSelector(
    (state: AppState) => state.areas.product.sort
  );

  const selectedVariantId = useSelector(
    (state: AppState) => state.areas.product.variant?.id
  );

  const getPriceString = (price?: number | null) => {
    if (!price) return `${t('product', 'price.from')} -`;
    return (
      <>
        {t('product', 'price.from')} <Currency price={price} />
      </>
    );
  };

  if (!group.variants?.length) return null;

  const renderVariants = (variants: VariantType[]) => {
    return variants.map((variant, index) => {
      return (
        <S.SelectableButton
          key={`${group.type}-${variant.id}`}
          handleOnClick={() => handleSelectedVariant(group, variant)}
          title={`${variant.name} (${variant.count})`}
          id={`${group.type}-${variant.id}`}
          name={group.type}
          selected={variant.id === selectedVariantId}
          image={
            group.type === 'color' && variant.data ? (
              <S.Color variantData={variant.data}>
                {variant.id === selectedVariantId ? <CheckIcon /> : null}
              </S.Color>
            ) : null
          }
          ariaLabel={`${group.name} - ${variant.name}`}
          order={index + 1}
          subtitle={
            currentSort === SortOptionEnum.PRICE_INCLUDES_SHIPPING
              ? getPriceString(variant?.price?.inclShipping)
              : getPriceString(variant?.price?.exclShipping)
          }
          type="radio"
          maxWidth={VARIANT_BUTTON_MAX_WIDTH}
        />
      );
    });
  };

  return (
    <S.VariantGroupWrapper>
      {isDesktopContent ? (
        <S.VariantGroup data-test="variant-group">
          {renderVariants(
            group.variants
              .sort((a, b) => a.id - b.id)
              .filter(
                (variant, index) => showAll || index < VISIBLE_VARIANTS_LIMIT
              )
          )}

          {group.variants.length > VISIBLE_VARIANTS_LIMIT ? (
            <S.ShowMore
              aria-label={
                showAll
                  ? t('common', 'button.show-less')
                  : t('common', 'button.show-more')
              }
              type="button"
              onClick={() => setShowAll(!showAll)}
              order={VISIBLE_VARIANTS_LIMIT}
            >
              {showAll ? (
                <ChevronUp />
              ) : (
                `+${group.variants.length - VISIBLE_VARIANTS_LIMIT}`
              )}
            </S.ShowMore>
          ) : null}
        </S.VariantGroup>
      ) : (
        <S.VariantGroup>
          {renderVariants(group.variants.sort((a, b) => a.id - b.id))}
        </S.VariantGroup>
      )}
    </S.VariantGroupWrapper>
  );
};

export default VariantGroups;
